import type { DecodedJwtClaims } from '@/lib/auth/auth-context'

/**
 * Decodifica o PAYLOAD de um JWT sem validar a assinatura.
 *
 * Por que não validar a assinatura: o JWT já foi validado pelo servidor
 * (Supabase Auth) que o emitiu — quem decide se ele é aceito é o backend
 * que consome o token, não o cliente. Aqui no frontend, decodificar é
 * APENAS para exibição (debug panel) e leitura de claims não-críticos
 * (ex.: `expiresAt` para countdown).
 *
 * Por que reimplementar em vez de instalar `jose` ou `jsonwebtoken`:
 * - `jose` pesa ~25 KB minificado e usa Web Crypto (a maior parte é para
 *   verificar assinatura — coisa que NÃO fazemos aqui).
 * - `jsonwebtoken` é Node-only.
 * - Decode puro de base64url + JSON cabe em 30 linhas e é 100% testável.
 *
 * Retorna `null` para QUALQUER input malformado (sem lançar) — chamadores
 * já tratam ausência de claims como "sessão sem visualização", então
 * propagar exceção só polui o caller.
 */
export function decodeJwtPayload(token: string | null | undefined): DecodedJwtClaims | null {
  if (!token) return null

  // JWT canônico = `<header>.<payload>.<signature>`. Aceitamos formatos
  // sem signature (`<header>.<payload>`) só por robustez, mas o Supabase
  // sempre emite os 3 segmentos.
  const parts = token.split('.')
  if (parts.length < 2) return null

  const payloadB64Url = parts[1]
  if (!payloadB64Url) return null

  // base64url → base64: troca chars URL-safe e adiciona padding.
  const padded = payloadB64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  const base64 = padded + padding

  let json: string
  try {
    // `atob` decodifica para uma binary string (1 byte por char). Pra
    // resgatar UTF-8 (emojis, acentos), passa por TextDecoder.
    const binary = atob(base64)
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0))
    json = new TextDecoder('utf-8').decode(bytes)
  } catch {
    return null
  }

  try {
    return JSON.parse(json) as DecodedJwtClaims
  } catch {
    return null
  }
}

/**
 * Devolve quanto tempo (em ms) falta até `expiresAt` (ISO 8601).
 * Negativo se já expirou. `null` se input inválido.
 *
 * Por que ms e não segundos: facilita comparar com `Date.now()` direto
 * sem cair em truncamento implícito.
 */
export function msUntilExpiry(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null
  const ts = Date.parse(expiresAt)
  if (Number.isNaN(ts)) return null
  return ts - Date.now()
}

/**
 * Formata milissegundos em string humanizada curta:
 *   3_600_000  → "1h 0m"
 *   125_000    → "2m 5s"
 *   -10_000    → "expirado"
 *
 * Usada no badge do header. Sempre 2 unidades pra caber visualmente.
 */
export function formatDuration(ms: number): string {
  if (ms <= 0) return 'expirado'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}
